import { useState, useEffect, useRef } from "react";
import BottomNav from "./BottomNav";
import { setupMobileTables } from "../lib/mobileTables";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { canAccessPath, isCraftsman, homePath } from "../lib/roles";
import { useAuth } from "../context/AuthContext";
import Sidebar from "./Sidebar";
import { SEARCH_SHORTCUT } from "../lib/platform";
import GlobalSearch from "./GlobalSearch";
import NotificationBell from "./NotificationBell";
import { Toaster } from "./ui/sonner";
import { Menu, Search as SearchIcon } from "lucide-react";
import BrandMark from "./BrandMark";
import OfflineBanner from "./OfflineBanner";
import UpdatePrompt from "./UpdatePrompt";

export default function Layout() {
    const { user, loading } = useAuth();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const touchStart = useRef(null);

    useEffect(() => setupMobileTables(), []);
    useEffect(() => { setSidebarOpen(false); }, [location.pathname]);
    useEffect(() => {
        document.body.style.overflow = sidebarOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [sidebarOpen]);
    const onTouchStart = (e) => { touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
    const onTouchEnd = (e) => {
        if (!touchStart.current) return;
        const dx = e.changedTouches[0].clientX - touchStart.current.x;
        const dy = Math.abs(e.changedTouches[0].clientY - touchStart.current.y);
        touchStart.current = null;
        if (dx < -70 && dy < 80) setSidebarOpen(false);  // gesto: tažení doleva zavře menu
    };

    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setSearchOpen(true);
            } else if (e.key === "Escape" && searchOpen) {
                setSearchOpen(false);
            }
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [searchOpen]);

    if (loading) {
        return <div className="h-screen flex items-center justify-center bg-slate-50">
            <div className="text-slate-500 text-sm uppercase tracking-widest">Načítání…</div>
        </div>;
    }
    if (!user) return <Navigate to="/prihlaseni" replace />;
    if (!canAccessPath(user, location.pathname)) return <Navigate to={homePath(user)} replace />;
    const craftsman = isCraftsman(user);

    return (
        <div className="flex min-h-screen bg-slate-50">
            {/* Desktop sidebar */}
            <div className="hidden md:block">
                <Sidebar />
            </div>
            {/* Mobile full-screen menu */}
            {sidebarOpen && (
                <div className="md:hidden fixed inset-0 z-[100] animate-fade-in" data-testid="mobile-menu" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                    <Sidebar onNavigate={() => setSidebarOpen(false)} onClose={() => setSidebarOpen(false)} />
                </div>
            )}

            <main className="flex-1 overflow-x-hidden min-w-0 pb-bottom-nav">
                {/* Top bar — odsazená pod stavovou lištu telefonu */}
                <div className="sticky top-0 z-40 bg-slate-900 text-slate-100 border-b border-slate-800 flex items-center px-3 md:px-6 py-1.5 md:py-2.5 gap-2 pt-safe px-safe" style={{ paddingTop: "calc(var(--safe-top) + 6px)" }}>
                    <button onClick={() => setSidebarOpen(true)} className="md:hidden min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-slate-800 rounded-sm" data-testid="mobile-menu-button" aria-label="Menu">
                        <Menu className="w-6 h-6" />
                    </button>
                    <div className="md:hidden flex-1 flex justify-center">
                        <img src="/ceramixa-logo.png" alt="Ceramixa style" className="h-7" />
                    </div>
                    {!craftsman && <button onClick={() => setSearchOpen(true)} data-testid="open-search"
                            className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-xs text-slate-400 min-w-[280px]">
                        <SearchIcon className="w-3.5 h-3.5" />
                        <span>Hledat ve firmě…</span>
                        <kbd className="ml-auto border border-slate-600 px-1.5 py-0.5 text-[10px] uppercase tracking-wider" data-testid="search-shortcut">{SEARCH_SHORTCUT}</kbd>
                    </button>}
                    {!craftsman && <button onClick={() => setSearchOpen(true)} className="md:hidden min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-slate-800 rounded-sm" data-testid="mobile-search-button" aria-label="Hledat">
                        <SearchIcon className="w-6 h-6" />
                    </button>}
                    <div className="flex-1 hidden md:block"></div>
                    <NotificationBell />
                </div>
                <OfflineBanner />
                <Outlet />
            </main>
            <BottomNav onMore={() => setSidebarOpen((o) => !o)} menuOpen={sidebarOpen} />
            {!craftsman && <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />}
            <UpdatePrompt />
            <Toaster position="top-right" />
        </div>
    );
}
